import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { TestConfiguration } from '../../interfaces/test-configuration.interface';
import { WorkflowConfigService } from '@ispm/workflow/services/workflow-config.service';
import { WorkflowActiveTabConfig } from '@ispm/workflow/interfaces/workflow-ui-config.interface';
import { isEmpty } from 'lodash';
import { IDiagnosticOrder, IRevision, IWorkflowRequest } from '../../interfaces/diagnostic-order.interface';
import { filter, takeUntil, switchMap, take } from 'rxjs/operators';
import { selectActiveWorkflow } from '@ispm/workflow/store/workflow-config.selectors';
import { DiagnosticOrderActionsService } from '@ispm/test/services/diagnostic-order-actions.service';
import { DiagnosticOrderService } from '@ispm/test/services/diagnostic-order.service';
import { TestService } from '../../services/test.service';
import { CURRENT_ORDER_ACTIONS } from '../../constants/current-order.constants';
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { WorkflowConfigState } from '@ispm/workflow/store/workflow-config.state';
import {ActivatedRoute, Router} from "@angular/router";
import {
  testViewPathologistPath,
  testViewBasePath,
  testViewWithPatientProfile
} from "@ispm/test/routes/test-view-state-names";
import {activeTabObject} from "@ispm/test/constants/Tabs.constants";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

@Component({
  selector: 'gen-test-view',
  templateUrl: './test-view.component.html',
  styleUrls: ['./test-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TestViewComponent implements OnInit, AfterViewInit, OnDestroy {

  isPatientContext: boolean = false;
  contextParam: string = 'orderId'; // TODO: Figure out how to do this as input by route
  contextName$: Observable<string>;
  contextId: string;
  workflowId: string;
  workflowRequest$: Observable<IWorkflowRequest>;
  currentRevision$: Observable<IRevision>;
  diagnosticOrder$: Observable<IDiagnosticOrder>;
  headerInfoSource$: Observable<any>;
  latestWorfklowRevisionId$: Observable<any>;
  testInWorklist$: Observable<boolean>;
  newNotesCount: number = 0;
  originalStateName: string;
  revisionId: string;
  currentTest: IDiagnosticOrder;
  currentUser: any; // Type user at some point.
  verticalLeftContainerPatientView: boolean;
  isFixedHeightTabPane: boolean;
  isCustomConfig: boolean = false;
  patient: any;
  public customRevisionData;
  public patientAnalyses;
  private destroyer$: Subject<any> = new Subject();
  public contextType: string = 'WorkflowRevision';
  public contextObject: any;
  private patientId: string;
  private testConfiguration: TestConfiguration;
  public activeWorkflowConfirmationButton: string = '';
  public isChecklistTabPresent: boolean = false;
  protected patientWithoutTests: boolean = false;
  pageReload: boolean = false;
  isAttachmentEnabled: boolean = false;

  constructor(
    public activatedRoute: ActivatedRoute,
    public testService: TestService,
    private store: Store<IgenomicsState>,
    private route: Router,
    private workflowService: WorkflowService,
    private workflowConfigService: WorkflowConfigService,
    private diagnosticOrderService: DiagnosticOrderService,
    private diagnosticOrderActionsService: DiagnosticOrderActionsService,
    private routerManagerService: RouterManagerService,
  ) {
    this.getWorkflowConfirmationButton();
    // Information of the page reloaded
    const navigation = this.route.getCurrentNavigation();
    if (navigation?.extras?.state) {
      this.pageReload = navigation.extras.state?.reload;
    }
  }

  /**
   * Return the getWorkflow Confirmation Button name
   */
  private getWorkflowConfirmationButton() {
    if (!this.activatedRoute.snapshot.data['isCustomConfig']) {
      this.store.select('workflowConfig').pipe(take(1)).subscribe((data: WorkflowConfigState) => {
        if (!isEmpty(data) && data.activeWorkflow.activeJobName) {
          this.activeWorkflowConfirmationButton = data.activeWorkflow.activeJobName;
          const activeTabs: WorkflowActiveTabConfig[] = data.activeWorkflow.revisions[data.activeWorkflow.revisions.length - 1].items.slice(-1).pop().ui?.activeTabs;

          if (activeTabs) {
            const findChecklistTab: WorkflowActiveTabConfig = activeTabs.find((tab: WorkflowActiveTabConfig) => tab.title === 'Checklist');
            this.isChecklistTabPresent = !!findChecklistTab;
          }
        }
      });
    }
  }

  ngOnInit() {
    //this.route.routeReuseStrategy.shouldReuseRoute = () => false;
    this.isCustomConfig = this.activatedRoute.snapshot.data['isCustomConfig'];
    this.patient = this.activatedRoute.snapshot.data['patient'];

    if (this.isCustomConfig) {
      if (isEmpty(this.activatedRoute.snapshot.data['tests'])) {
        this.patientWithoutTests = true;
      }
      this.patientAnalyses = this.activatedRoute.snapshot.data['tests'];

      this.workflowConfigService.getActiveWorkflow()
        .pipe(
          filter(workflowRequest => !!workflowRequest && !!this.patientAnalyses && !!this.patientAnalyses.current),
          take(1)
        )
        .subscribe(workflowRequest => {
          this.patientAnalyses.current.workflowRequest = workflowRequest;
        });
      this.customRevisionData = this.patientWithoutTests ? undefined : this.setCurrentRevisionInfo();
    }

    this.testConfiguration = this.testService.testConfiguration;

    if (this.testConfiguration) {
      this.isFixedHeightTabPane = !this.testConfiguration.isPathwayTest;
      this.verticalLeftContainerPatientView = !!this.testConfiguration.VerticalLeftContainerPatientView;
    }

    this.currentUser = JSON.parse(localStorage.user);
    this.revisionId = this.routerManagerService.parameters.revisionId;
    this.workflowId = this.routerManagerService.parameters.testId;

    const currentAppId = JSON.parse(sessionStorage.getItem('currentApplication')).id;

    this.getOrganizationDataFromStore().pipe(take(1)).subscribe(org => {
      const currentAppConfig = org.applications.find(app => app.appId == currentAppId);
      if (currentAppConfig.notesContextType) {
        this.contextType = currentAppConfig.notesContextType;
      }
      this.setContextId()
      this.buildContextObject();
    });
    this.isPatientContext = this.activatedRoute.snapshot.data['isPatientContext'];
    this.contextParam = this.activatedRoute.snapshot.data['contextParam'] ? this.routerManagerService.parameters[this.activatedRoute.snapshot.data['contextParam']] : 'orderId';

    if (!this.contextParam) {
      console.error('[TestViewComponent] - Context parameter is mandatory for test view!');
    }

    this.patientId = this.contextParam;

    if (this.isPatientContext) {
      this.initWithPatientContext(testViewWithPatientProfile);
    } else if (this.isCustomConfig) {
      this.initWithPatientContext(testViewPathologistPath);
    } else {
      this.initWithOrderContext();
    }

    this.workflowRequest$ = this.store.pipe(
      takeUntil(this.destroyer$.asObservable()),
      select(selectActiveWorkflow),
      filter(workflow => !!workflow)
    );
    this.currentRevision$ = this.workflowRequest$.map(workflow => workflow.revisions.find(revision =>
      revision.id === this.routerManagerService.parameters.revisionId));
    this.latestWorfklowRevisionId$ = this.workflowRequest$.map(workflow => workflow.revisions[workflow.revisions.length - 1].id);
    this.initTestInWorklist();
    this.diagnosticOrder$.pipe(takeUntil(this.destroyer$.asObservable())).subscribe((order: IDiagnosticOrder) => {
      this.workflowService.currentTest = order;
      this.currentTest = order;
    });
  }

  ngOnDestroy() {
    this.destroyer$.next();
    this.destroyer$.complete();
    this.onExit();
  }

  /**
   * On Exit is triggered on ngDestroy Lifecycle which will be triggered for reload, is a problem
   *
   * this part is called in ExitGuard from test-view-routes-resolvable-exports.ts
   */
  onExit() {
    if (
        (this.routerManagerService.currentState === this.routerManagerService.selectedState)
        || (this.routerManagerService.selectedState.split('/').length < 3 && !this.routerManagerService.stateBeforeReload)
    ) {
      this.testService.activeTab = activeTabObject.TEST_INFO;
    }
  }

  updateNotes(notes: any) {
    if (notes) {
      this.newNotesCount = notes.filter((note) => !note.read).length;
    } else {
      this.newNotesCount = 0;
    }
  }

  private buildContextObject() {
    this.contextObject = [
      {
        "reference": this.revisionId,
        "resourceType": "WorkflowRevision"
      },
      {
        "reference": this.workflowId,
        "resourceType": "WorkflowRequest"
      }
    ]
  }

  private setContextId() {
    switch (this.contextType.toLowerCase()) {
      case 'workflowrequest':
        this.contextId = this.workflowId;
        break;
      case 'patient':
        this.contextId = this.patientId;
        break;
      default:
        this.contextId = this.revisionId;
        break;
    }
  }

  private getOrganizationDataFromStore(): Observable<any> {
    return this.store.select('users','organizationConfiguration');
  }

  private initWithPatientContext(path) {
    this.originalStateName = path;
    this.initDiagnosticOrder();
    this.contextName$ = this.store.select('users', 'patients')
      .map(patients => patients.get(this.patientId))
      .filter(patient => !!patient)
      .map(patient => patient.name[0].text);
    this.headerInfoSource$ = this.diagnosticOrder$.map(order => ({patientAnalyses: {current: order}}));
  }

  private initWithOrderContext() {
    this.originalStateName = testViewBasePath;
    this.initDiagnosticOrder();
    this.contextName$ = this.diagnosticOrder$.filter(order => !isEmpty(order.identifiers)).map(order => order.identifiers[0].value);
    this.headerInfoSource$ = this.diagnosticOrder$.map(order => ({patientAnalyses: {current: order}}));
  }

  private initDiagnosticOrder() {
    this.diagnosticOrder$ = this.store.select('tests', 'diagnosticOrders', 'order')
      .pipe(
        filter((order: IDiagnosticOrder) => !!order),
        switchMap((order: IDiagnosticOrder) => {

        // check if the diagnosticOrder need to be updated with the 'showDuplicatePatient' indication.
        // updating this indication is required to avoid displaying a duplicate message warning multiple times. If this
        //  message was already shown, the 'showDuplicatePatient' flag was set off in the diagnostic-order's configuration
        // if (order['showDuplicatePatient'] != false) {
        //   return this.updateDiagnosticOrder({id: order.id, showDuplicatePatient: false});
        // }

        // in case that 'showDuplicatePatient' is false, return the original order data
        return Observable.of(order);
      })
      );
  }

  private updateDiagnosticOrder(diagnosticOrderUpdatedData: any): Observable<IDiagnosticOrder> {
    return new Observable<IDiagnosticOrder>(observer => {
      // send request to update the diagnostic order's data
      this.diagnosticOrderService.updateDiagnosticOrderFields(diagnosticOrderUpdatedData)
        .pipe(
          take(1)
        )
        .subscribe(
          (newOrder) => {

            // on success, update also the diagnostic order's data to the local storage
            const metaData = newOrder['meta'];
            if (!!metaData) diagnosticOrderUpdatedData.meta = metaData;

            this.diagnosticOrderActionsService.updateDiagnosticOrderFields(diagnosticOrderUpdatedData);

            // read & return the update diagnosticOrder data from the local storage
            this.diagnosticOrderActionsService.getState()
              .pipe(
                take(1),
                filter(res => !!res && !!res.order)
              )
              .subscribe(res => {
                observer.next(res.order);
                observer.complete();
              });
          },
          (err: any) => {
            console.error('Failed to update diagnosticOrder - ' + err);
            observer.error();
          }
        );

    });
  }

  private initTestInWorklist() {
    this.testInWorklist$ = this.workflowRequest$.map(workflow => workflow.references)
      .map((references: any[]) => references ? references.some(ref => {
        if (ref.type !== 'practitioner') {
          return false;
        }

        const refValue = JSON.parse(ref.value);

        return (refValue.practType === 'orderer' || refValue.practType === 'performer') && this.currentUser.info.id === refValue.practId;
      }) : false);
  }

  notesPanelOpened() {
    this.newNotesCount = 0;
  }

  enableAttachment(evt) {
    this.isAttachmentEnabled = evt;
  }

  private setCurrentRevisionInfo() {
    let currentRevision = this.patientAnalyses.current.workflowRequest.revisions.find((revision) => {
      return revision.id === this.routerManagerService.parameters.revisionId;
    });
    const isActiveRevision = this.patientAnalyses.current.workflowRequest.activeRevision === this.routerManagerService.parameters.revisionId;
    let currentJobIndex, currentRevisionLastJobIndex = -1;
    currentRevision?.items?.reverse();

    if (isActiveRevision) {
      currentJobIndex = currentRevision.items.findIndex(item => item.id === this.patientAnalyses.current.workflowRequest.activeJob);
    } else {
      currentJobIndex = currentRevision.items.findIndex(item => item.initialStatusMsg.toLowerCase() === currentRevision.status.toLowerCase());
      currentRevisionLastJobIndex = currentRevision.items.findIndex(item => item.status.toLowerCase() === 'done');
    }

    if (currentJobIndex !== -1) {
      currentRevision.activeJob = currentRevision.items[currentJobIndex];
    } else if ((!isActiveRevision || (isActiveRevision && currentJobIndex !== -1)) && currentRevisionLastJobIndex > 0 && currentRevisionLastJobIndex < currentRevision.items.length) {
      currentRevision.activeJob = currentRevision.items[currentRevisionLastJobIndex - 1];// we need the step before the found one since we found the first done
    } else {
      currentRevision.activeJob = {name: currentRevision.status, type: currentRevision.status};
      currentRevision.items.reverse();//returning to initial order
      currentRevision.humanReadableMessage = this.humanReadableMessageToPlainText(currentRevision.humanReadableMessage);
    }
    return currentRevision;
  }

  private humanReadableMessageToPlainText(inputMessage) {
    let result;
    try {
      result = JSON.parse(inputMessage).plainText;
    } catch (e) {
      result = inputMessage;
    }
    return result;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      // Clear current order details from the store after component was loaded (its data should be used only while loading)
      this.store.dispatch({type: CURRENT_ORDER_ACTIONS.INIT_CURRENT_ORDER_STATE});
    }, 3000);
  }
}
